import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StreamPageHeader from './partial/stream/StreamPageHeader.jsx';
import LoadingSpinner from './partial/LoadingSpinner.jsx';
import '../styles/RegisterBand.css';
import config from '../config.js';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { findSocialMediaLinkError, normalizeSocialMediaUrl } from '../_helpers/socialMediaLinks.js';
import { useT } from '../contexts/LocaleContext.jsx';
import { useCountryEligibility } from '../hooks/useCountryEligibility.js';
import MusicianWaitlist from './partial/MusicianWaitlist.jsx';

/** The page's own form treatment, lent to the waitlist card that replaces the form. */
const WAITLIST_CLASS_NAMES = {
  title: 'register-band-title',
  label: 'register-band-label',
  required: 'register-band-required',
  input: 'register-band-input',
  textarea: 'register-band-input register-band-textarea',
  error: 'register-band-error',
  primary: 'register-band-btn-primary',
  secondary: 'register-band-btn-secondary',
};

function RegisterBand() {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [websiteURL, setWebsiteURL] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [socialMediaLinks, setSocialMediaLinks] = useState(['']);
  const [defaultloginPage, setDefaultloginPage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // The role this account needs to make a band (ENG-892 links it to the Upgrade page).
  const [needsPerformer, setNeedsPerformer] = useState(false);

  const fetchWrapper = useFetchWrapper();
  const navigate = useNavigate();
  const t = useT();
  // Whether this account's country is open to performers (ENG-375). The form
  // is only withheld for a country the backend named as closed; a check that
  // failed, one this deployment does not serve and a signed-out visitor all
  // leave the page exactly as it was.
  const {
    country,
    eligibility,
    waitlist,
    signupOpen,
    checking,
    joining,
    joinError,
    join,
  } = useCountryEligibility();

  useEffect(() => {
    document.body.classList.add('register-band-page-body');
    return () => document.body.classList.remove('register-band-page-body');
  }, []);

  const handleAddSocialMediaLink = () => {
    setSocialMediaLinks([...socialMediaLinks, '']);
  };

  // The first row stays; the ones added after it can be taken away (REG-06).
  const handleRemoveSocialMediaLink = (index) => {
    setSocialMediaLinks((links) => links.filter((_, i) => i !== index));
  };

  const handleSocialMediaLinkChange = (index, value) => {
    const newLinks = [...socialMediaLinks];
    newLinks[index] = value;
    setSocialMediaLinks(newLinks);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim() || !description.trim() || !location.trim() || !contactEmail.trim()) {
      setError(t('registerBand.errors.requiredFields'));
      return;
    }

    // Same allowlist the backend enforces, checked here so the member is told
    // which link is the problem before the request is made.
    const socialMediaLinkError = findSocialMediaLinkError(socialMediaLinks, t);
    if (socialMediaLinkError) {
      setError(socialMediaLinkError);
      return;
    }

    // Send links trimmed and with the https:// people leave off, dropping empty rows
    const filteredSocialMediaLinks = socialMediaLinks.map(normalizeSocialMediaUrl).filter(Boolean);

    setError('');
    setNeedsPerformer(false);
    setLoading(true);

    try {
      await fetchWrapper.post(`${config.apiBaseUrl}/bands`, {
        name,
        description,
        location,
        websiteURL,
        contactEmail,
        socialMediaLinks: filteredSocialMediaLinks,
        defaultloginPage
      });

      navigate('/performers');
    } catch (err) {
      // Only performers can create a band (backend ENG-755).
      if (err?.code === 'performer_role_required') {
        setError(t('registerBand.errors.performerRequired'));
        setNeedsPerformer(true);
      }
      else setError(err?.message || t('common.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-band-page">
      <div className="register-band-page-header">
        <StreamPageHeader />
      </div>
      <div className="register-band-page-wrapper">
        <div className="register-band-card">
          <h1 className="register-band-title">{t('registerBand.title')}</h1>

          {checking ? (
            <div className="register-band-notice" role="status" data-testid="register-band-checking">
              {t('registerBand.waitlist.checking')}
            </div>
          ) : !signupOpen ? (
            <MusicianWaitlist
              country={country}
              eligibility={eligibility}
              waitlist={waitlist}
              joining={joining}
              joinError={joinError}
              onJoin={join}
              classNames={WAITLIST_CLASS_NAMES}
            />
          ) : (
            <>
              <div className="register-band-notice" role="status">
                {t('registerBand.publicNotice')}
              </div>

              <form className="register-band-form" onSubmit={handleSubmit} data-testid="register-band-form">
                {error && (
                  <div className="register-band-error" role="alert" data-testid="register-band-error">
                    {error}
                    {needsPerformer && (
                      <>
                        {' '}
                        <Link to="/upgrade?plan=performer" data-testid="register-band-upgrade">{t('registerBand.errors.seePlans')}</Link>
                      </>
                    )}
                  </div>
                )}

                <div className="register-band-form-row">
                  <div className="register-band-field">
                    <label className="register-band-label" htmlFor="register-band-name">
                      {t('registerBand.form.nameLabel')} <span className="register-band-required" aria-hidden="true">*</span>
                    </label>
                    <input
                      id="register-band-name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="register-band-input"
                      placeholder={t('registerBand.form.namePlaceholder')}
                      required
                      aria-required="true"
                    />
                  </div>
                  <div className="register-band-field">
                    <label className="register-band-label" htmlFor="register-band-location">
                      {t('registerBand.form.locationLabel')} <span className="register-band-required" aria-hidden="true">*</span>
                    </label>
                    <input
                      id="register-band-location"
                      type="text"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      className="register-band-input"
                      placeholder={t('registerBand.form.locationPlaceholder')}
                      required
                      aria-required="true"
                    />
                  </div>
                </div>

                <div className="register-band-form-row">
                  <div className="register-band-field">
                    <label className="register-band-label" htmlFor="register-band-website">
                      {t('registerBand.form.websiteLabel')}
                    </label>
                    <input
                      id="register-band-website"
                      type="url"
                      value={websiteURL}
                      onChange={(e) => setWebsiteURL(e.target.value)}
                      className="register-band-input"
                      placeholder={t('registerBand.form.urlPlaceholder')}
                    />
                  </div>
                  <div className="register-band-field">
                    <label className="register-band-label" htmlFor="register-band-email">
                      {t('registerBand.form.contactEmailLabel')} <span className="register-band-required" aria-hidden="true">*</span>
                    </label>
                    <input
                      id="register-band-email"
                      type="email"
                      value={contactEmail}
                      onChange={(e) => setContactEmail(e.target.value)}
                      className="register-band-input"
                      placeholder={t('registerBand.form.contactEmailPlaceholder')}
                      required
                      aria-required="true"
                    />
                  </div>
                </div>

                <div className="register-band-field">
                  <label className="register-band-label" htmlFor="register-band-description">
                    {t('registerBand.form.descriptionLabel')} <span className="register-band-required" aria-hidden="true">*</span>
                  </label>
                  <textarea
                    id="register-band-description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="register-band-input register-band-textarea"
                    placeholder={t('registerBand.form.descriptionPlaceholder')}
                    required
                    aria-required="true"
                  />
                </div>

                <div className="register-band-field">
                  <span className="register-band-label">{t('registerBand.form.socialMediaLinksLabel')}</span>
                  {/* type="text", not "url": the browser's url check would refuse
                      a pasted "instagram.com/theband" before handleSubmit can add
                      the https:// (and it lets javascript: through anyway); the
                      allowlist check is the real validation. */}
                  <div className="register-band-social-row">
                    <input
                      type="text"
                      inputMode="url"
                      value={socialMediaLinks[0]}
                      onChange={(e) => handleSocialMediaLinkChange(0, e.target.value)}
                      className="register-band-input"
                      placeholder={t('registerBand.form.urlPlaceholder')}
                      aria-label={t('registerBand.form.socialMediaLinkLabel', { index: 1 })}
                    />
                    <button
                      type="button"
                      onClick={handleAddSocialMediaLink}
                      className="register-band-btn-add"
                      aria-label={t('registerBand.form.addSocialMediaLinkLabel')}
                    >
                      {t('registerBand.form.addSocialMediaLink')}
                    </button>
                  </div>
                  {socialMediaLinks.length > 1 && (
                    <div className="register-band-social-extra">
                      {socialMediaLinks.slice(1).map((link, i) => (
                        <div key={i + 1} className="register-band-social-row">
                          <input
                            type="text"
                            inputMode="url"
                            value={link}
                            onChange={(e) => handleSocialMediaLinkChange(i + 1, e.target.value)}
                            className="register-band-input"
                            placeholder={t('registerBand.form.urlPlaceholder')}
                            aria-label={t('registerBand.form.socialMediaLinkLabel', { index: i + 2 })}
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveSocialMediaLink(i + 1)}
                            className="register-band-btn-remove"
                            aria-label={t('registerBand.form.removeSocialMediaLinkLabel', { index: i + 2 })}
                          >
                            <span aria-hidden="true">×</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="register-band-checkbox-field">
                  <input
                    type="checkbox"
                    id="default-login-page"
                    checked={defaultloginPage}
                    onChange={(e) => setDefaultloginPage(e.target.checked)}
                    className="register-band-checkbox"
                  />
                  <label htmlFor="default-login-page" className="register-band-checkbox-label">
                    {t('registerBand.form.defaultLoginPageLabel')}
                  </label>
                </div>

                <div className="register-band-actions">
                  <button
                    type="button"
                    className="register-band-btn-secondary"
                    onClick={() => navigate('/performers')}
                  >
                    {t('common.actions.cancel')}
                  </button>

                  {loading ? (
                    <LoadingSpinner />
                  ) : (
                    <button type="submit" className="register-band-btn-primary">
                      {t('registerBand.actions.createProfile')}
                    </button>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default RegisterBand;
